import config from '@/config'
import { expiryDatePayload } from '@/lib/dateInput.js'
/** Корень досье (Structure) — см. VITE_DOSSIER_STORAGE (ltms). */
const adapter = config.VITE_DOSSIER_STORAGE

/** Контексты таблиц стартовой страницы → подпись поля «Статус» в карточке ЛП. */
export const CARD_CATEGORIES = {
  planned_maa: {
    key: 'planned_maa',
    titleKey: 'kpi.plannedNewMaa',
    kpiTitleKey: 'kpi.plannedNewMaa',
    statusLabelKey: 'kpi.plannedNewMaa',
    borderClass: 'planned',
    filter: { card_category: 'planned_maa' },
  },
  new_maa_registration: {
    key: 'new_maa_registration',
    titleKey: 'kpi.submittedNewMaa',
    kpiTitleKey: 'kpi.submittedNewMaa',
    statusLabelKey: 'kpi.submittedNewMaa',
    borderClass: 'planned',
    filter: { card_category: 'new_maa_registration' },
  },
  post_approval: {
    key: 'post_approval',
    titleKey: 'kpi.variation',
    kpiTitleKey: 'kpi.variation',
    statusLabelKey: 'kpi.variation',
    borderClass: 'postapproval',
    filter: { card_category: 'post_approval' },
  },
  registered: {
    key: 'registered',
    titleKey: 'kpi.variation',
    kpiTitleKey: 'kpi.variation',
    statusLabelKey: 'kpi.variation',
    borderClass: 'registered',
    filter: { card_category: 'registered' },
  },
}

/** KPI-плашки над блоками (без Post-approval). */
export const HOME_KPI_KEYS = ['planned_maa', 'new_maa_registration', 'registered']

/**
 * Папки презентаций в корне досье (AS-1444).
 * Со стартовой — отдельные формы; path нужен для загрузки из Finder API.
 */
export const PRESENTATION_FOLDER_LINKS = [
  {
    labelKey: 'presentations.regulatoryCommittee',
    path: `${adapter}://Committee presentations`,
    routeName: 'presentations.committee',
    folderKey: 'committee',
    icon: 'bi bi-easel2-fill',
  },
  {
    labelKey: 'presentations.regIntel',
    path: `${adapter}://Regulatory Intelligence`,
    routeName: 'presentations.regIntel',
    folderKey: 'regIntel',
    icon: 'bi bi-diagram-3-fill',
  },
  {
    labelKey: 'presentations.localSops',
    /** Каталог Veeva лежит в досье: ltms://Local SOPs/{SOPs|WIs|Forms}. */
    path: `${adapter}://Local SOPs`,
    routeName: 'sops.list',
    icon: 'bi bi-journal-bookmark-fill',
  },
]

/** Action-bar стартовой: Committee / RegIntel / Local SOPs (порядок как в макете). */
export const HOME_ACTION_BAR_LINKS = PRESENTATION_FOLDER_LINKS.filter(
  link => link.homeActionBar !== false
)

/** Роли с доступом к папкам презентаций (ACL + плашки на стартовой). */
export const PRESENTATION_FOLDER_ACCESS_ROLES = [
  'regspec',
  'reglead',
  'globalteam',
  'dossier_presentations_view',
  'sa',
]

/** Переключатели блока Planned (UI; фильтрация — позже). */
export const PLANNED_PIPELINE_MODES = [
  { key: 'new_maa', labelKey: 'pipeline.newMaa' },
  { key: 'new_indications', labelKey: 'pipeline.newIndications' },
]

/** Заготовка правой колонки — Analytical Reports (данные позже). */
export { HOME_ANALYTICS_REPORTS } from '@/lib/analyticsReports'

export const HOME_KPI_META = {
  planned_maa: { icon: 'bi bi-clock', tone: 'planned' },
  new_maa_registration: { icon: 'bi bi-envelope', tone: 'registration' },
  registered: { icon: 'bi bi-check-lg', tone: 'registered' },
}

export const RISK_CATEGORY_KEYS = [
  { key: 'local_ct', labelKey: 'risk.localCt' },
  { key: 'eaeu_gmpc', labelKey: 'risk.eaeuGmpc' },
  { key: 'shelf_life', labelKey: 'risk.shelfLife' },
  { key: 'samples', labelKey: 'risk.samples' },
  { key: 'trade_name', labelKey: 'risk.tradeName' },
]

export const RSD_CUTOFF = new Date(2026, 3, 20) // 20.04.2026 (локальная календарная дата)

/**
 * SMoH: статус Approved/Completed или этап «Одобрение» —
 * Prototype of Indications из SoH не копируется.
 *
 * @param {object|null|undefined} smoh card_smoh (status / stage).
 * @returns {boolean}
 */
export function isSmohApproved(smoh) {
  if (!smoh) {
    return false
  }

  const status = smoh.status
  if (status != null) {
    const key = String(status.key || '').trim().toLowerCase()
    const value = String(status.value || '').trim().toLowerCase()
    if (
      key === 'approved' ||
      key === 'completed' ||
      key.startsWith('approved:') ||
      key.startsWith('approved_') ||
      key.startsWith('completed:') ||
      key.startsWith('completed_') ||
      value === 'approved' ||
      value === 'completed' ||
      value.startsWith('approved ') ||
      value.startsWith('completed ') ||
      value.startsWith('одобрено') ||
      value.startsWith('завершено')
    ) {
      return true
    }
  }

  const stage = smoh.stage
  if (stage != null) {
    const key = String(stage.key || '').trim().toLowerCase()
    const value = String(stage.value || '').trim().toLowerCase()
    if (
      key === 'approval' ||
      key.startsWith('approval:') ||
      value === 'approval' ||
      value.startsWith('одобрение')
    ) {
      return true
    }
  }

  return false
}

/**
 * Показывать ли Prototype of Indications из SoH (после Approved / этапа Одобрение — нет).
 * @param {object|null|undefined} smoh
 */
export function shouldShowSmohPrototype(smoh) {
  if (!smoh || smoh.submission_id == null) {
    return false
  }
  if (isSmohApproved(smoh)) {
    return false
  }
  if (Object.prototype.hasOwnProperty.call(smoh, 'show_prototype')) {
    return Boolean(smoh.show_prototype)
  }
  return true
}

/**
 * Текст Prototype of indications EN/RU из SoH.
 * preferEnglish: UI locale en (глобал по умолчанию + переключатель языка).
 */
export function smohPrototypeText(smoh, preferEnglish) {
  if (!shouldShowSmohPrototype(smoh)) {
    return ''
  }
  return localizedPair(
    smoh.prototype_of_indications_en,
    smoh.prototype_of_indications_ru,
    preferEnglish
  )
}

/** Прочерк для пустых значений карточки. */
export function dash(value) {
  if (value === null || value === undefined) {
    return '—'
  }
  const text = String(value).trim()
  return text || '—'
}

/**
 * Разбивает Clinical indications на отдельные значения для карточки ЛП.
 * Несколько значений — каждое с новой строки (ТЗ); учитывает переносы и склейку ", "
 * (миграция v18 / прежний join на фронте).
 *
 * @param {string} text Свободный текст Clinical indications.
 * @returns {string[]} Непустые значения по строкам.
 */
export function splitClinicalIndicationLines(text) {
  const raw = String(text || '').trim()
  if (!raw) {
    return []
  }
  const byNewline = raw
    .split(/\r?\n/)
    .map(part => part.trim())
    .filter(Boolean)
  if (byNewline.length > 1) {
    return byNewline
  }
  const single = byNewline[0] || raw
  if (!single.includes(', ')) {
    return [single]
  }
  return single
    .split(', ')
    .map(part => part.trim())
    .filter(Boolean)
}

/** Значение relation/dropdown или скаляра. */
export function relationText(value) {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }
  if (typeof value === 'object') {
    return String(value.value ?? value.fio ?? value.username ?? value.key ?? '')
  }
  return String(value)
}

/** EN/RU поле в зависимости от preferEnglish (глобальный пользователь). */
export function localizedPair(en, ru, preferEnglish) {
  const enText = relationText(en)
  const ruText = relationText(ru)
  if (preferEnglish) {
    return enText || ruText
  }
  return ruText || enText
}

/**
 * Product (INN/Trade Name): одно поле по приоритету ТЗ.
 * Торговое наименование → МНН → код молекулы (azd_code).
 */
export function productDisplayName(product, preferEnglish) {
  if (!product) {
    return ''
  }
  return (
    localizedPair(product.trade_name_en, product.trade_name_ru, preferEnglish) ||
    localizedPair(product.inn_en, product.inn_ru, preferEnglish) ||
    relationText(product.azd_code) ||
    ''
  )
}

/**
 * Имя в заголовке карточки PL: одно значение по приоритету.
 * Trade name → МНН → код молекулы (azd_code).
 * Trade name и МНН — парные EN/RU: язык как у переключателя (preferEnglish).
 *
 * @param {object|null|undefined} product Карточка продукта.
 * @param {boolean} [preferEnglish=true] UI locale en.
 * @returns {string}
 */
export function productCardHeading(product, preferEnglish = true) {
  return productDisplayName(product, preferEnglish)
}

/** Дозировка из nested dosage_forms. */
export function pickStrength(product, preferEnglish) {
  const forms = product?.dosage_forms || []
  const values = []
  for (const form of forms) {
    for (const item of form.items || []) {
      const value = localizedPair(item.strength_en, item.strength_ru, preferEnglish)
      if (value) {
        values.push(value)
      }
    }
  }
  return values.join('; ')
}

/** Конкретная языковая сторона дозировки из nested dosage_forms. */
export function pickStrengthField(product, field) {
  const forms = product?.dosage_forms || []
  const values = []
  for (const form of forms) {
    for (const item of form.items || []) {
      const value = relationText(item?.[field])
      if (value) {
        values.push(value)
      }
    }
  }
  return values.join('; ')
}

/** Лекарственная форма из nested dosage_forms. */
export function pickDosageForm(product, preferEnglish) {
  const forms = product?.dosage_forms || []
  const values = []
  for (const form of forms) {
    const value = localizedPair(form.dosage_form_en, form.dosage_form_ru, preferEnglish)
    if (value) {
      values.push(value)
    }
  }
  return values.join('; ')
}

/** Конкретная языковая сторона лекарственной формы из nested dosage_forms. */
export function pickDosageFormField(product, field) {
  const forms = product?.dosage_forms || []
  const values = []
  for (const form of forms) {
    const value = relationText(form?.[field])
    if (value) {
      values.push(value)
    }
  }
  return values.join('; ')
}

/** Календарная дата без времени/TZ — для сравнения с порогом RSD. */
function toDateOnly(value) {
  if (value == null || value === '') {
    return null
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const raw = String(value).trim()
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
  }
  const dmy = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})/)
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
  }
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

/** Кнопка RSD: скрыта для препаратов с Initial Registration date < 20.04.2026. */
export function isRsdVisible(product) {
  const date = toDateOnly(product?.initial_registration_date)
  if (!date) {
    return true
  }
  return date >= RSD_CUTOFF
}

export function emptyIndication(sort = 1) {
  return {
    id: null,
    sort,
    text: '',
    short_text_ru: '',
    full_text_ru: '',
    short_text_en: '',
    full_text_en: '',
    status: null,
  }
}

/**
 * Нормализует indications API → UI-строки.
 * Старый text переносится в короткое показание RUS.
 *
 * @param {Array|undefined|null} list Indications с бэка.
 * @param {() => string} makeKey Генератор ключа строки (uuid).
 * @returns {Array} Строки для формы.
 */
export function hydrateIndicationRows(list, makeKey) {
  const keyFn = typeof makeKey === 'function' ? makeKey : () => String(Math.random())
  const rows = (list || []).map(item => ({
    ...emptyIndication(item.sort || 1),
    ...item,
    _key: item.id || item._key || keyFn(),
    short_text_ru: item.short_text_ru || item.text || '',
    full_text_ru: item.full_text_ru || '',
    short_text_en: item.short_text_en || '',
    full_text_en: item.full_text_en || '',
    text: item.short_text_ru || item.text || '',
  }))
  return rows.length
    ? rows
    : [{ ...emptyIndication(1), _key: keyFn() }]
}

/** Пустой черновик на форме не считается показанием. */
function indicationHasContent(item) {
  return ['short_text_ru', 'full_text_ru', 'short_text_en', 'full_text_en', 'text'].some(
    key => String(item?.[key] ?? '').trim()
  )
}

/**
 * Сериализация UI-строк indications для PUT products.
 * Статус с формы не отправляется и на сервере не затирается.
 * Пустая строка-заготовка без id не уходит в payload: иначе новая карточка
 * получает запись «показание добавлено», хотя пользователь его не заполнял.
 *
 * @param {Array} rows UI-строки.
 * @returns {Array} Payload для sync_indications.
 */
export function serializeIndicationRows(rows) {
  return (rows || [])
    .filter(item => item?.id || indicationHasContent(item))
    .map((item, index) => {
      const shortRu = item.short_text_ru || ''
      return {
        id: item.id || undefined,
        sort: index + 1,
        short_text_ru: shortRu,
        full_text_ru: item.full_text_ru || '',
        short_text_en: item.short_text_en || '',
        full_text_en: item.full_text_en || '',
        text: shortRu,
      }
    })
}

export function emptyRiskCategories() {
  return Object.fromEntries(
    RISK_CATEGORY_KEYS.map(({ key }) => [key, { level: '', comment: '' }])
  )
}

export function normalizeRiskCategories(value) {
  const base = emptyRiskCategories()
  if (!value || typeof value !== 'object') {
    return base
  }
  for (const { key } of RISK_CATEGORY_KEYS) {
    const entry = value[key] || {}
    base[key] = {
      level: entry.level || '',
      comment: entry.comment || '',
    }
  }
  if (value.rsd_notes) {
    base.rsd_notes = value.rsd_notes
  }
  return base
}

export function buildRiskPayload(riskCategories) {
  const risk = {}
  for (const { key } of RISK_CATEGORY_KEYS) {
    risk[key] = {
      level: riskCategories?.[key]?.level || '',
      comment: riskCategories?.[key]?.comment || '',
    }
  }
  return { risk_categories: risk }
}

export function buildCardPayload(model) {
  const risk = { ...(model.risk_categories || {}) }
  for (const { key } of RISK_CATEGORY_KEYS) {
    risk[key] = {
      level: model.risk_categories?.[key]?.level || '',
      comment: model.risk_categories?.[key]?.comment || '',
    }
  }
  return {
    indications: serializeIndicationRows(model.indications),
    risk_categories: risk,
    clinical_trials: serializeClinicalTrials(model.clinical_trials),
    market_access: serializeMarketAccess(model.market_access),
    product_journey: serializeProductJourney(model.product_journey),
    full_access: Boolean(model.full_access),
  }
}

export const PRODUCT_JOURNEY_STAGES = ['clinical', 'market_access', 'release']
export const PRODUCT_JOURNEY_DATE_KEYS = [
  'submission_plan',
  'submission_actual',
  'approval_plan',
  'approval_actual',
]

export function emptyJourneyStage() {
  return Object.fromEntries(PRODUCT_JOURNEY_DATE_KEYS.map(key => [key, null]))
}

export function emptyProductJourney() {
  return {
    clinical: emptyJourneyStage(),
    market_access: emptyJourneyStage(),
    release: emptyJourneyStage(),
    prototype_comment: '',
  }
}

export function normalizeProductJourney(value) {
  const base = emptyProductJourney()
  if (!value || typeof value !== 'object') {
    return base
  }
  for (const stage of PRODUCT_JOURNEY_STAGES) {
    const raw = value[stage] || {}
    base[stage] = emptyJourneyStage()
    for (const key of PRODUCT_JOURNEY_DATE_KEYS) {
      base[stage][key] = raw[key] || null
    }
  }
  base.prototype_comment = String(value.prototype_comment || '').trim()
  return base
}

export function serializeProductJourney(value) {
  const normalized = normalizeProductJourney(value)
  const out = {
    prototype_comment: normalized.prototype_comment || '',
  }
  for (const stage of PRODUCT_JOURNEY_STAGES) {
    out[stage] = { ...normalized[stage] }
  }
  return out
}

export function journeyStageHasDates(stage) {
  if (!stage || typeof stage !== 'object') {
    return false
  }
  return PRODUCT_JOURNEY_DATE_KEYS.some(key => {
    const v = stage[key]
    return v != null && String(v).trim() !== ''
  })
}

export function emptyMarketAccess() {
  return {
    vednp: { value: 'no', plan_date: null, actual_date: null },
    clinRecs: { value: 'no', plan_date: null, actual_date: null },
  }
}

export function normalizeMarketAccess(value) {
  const base = emptyMarketAccess()
  if (!value || typeof value !== 'object') {
    return base
  }
  for (const key of ['vednp', 'clinRecs']) {
    const block = value[key] || {}
    let status = String(block.value || block.status || 'no').toLowerCase()
    if (['true', '1', 'yes', 'да'].includes(status)) {
      status = 'yes'
    } else {
      status = 'no'
    }
    base[key] = {
      value: status,
      plan_date: block.plan_date || block.planDate || null,
      actual_date: block.actual_date || block.actualDate || null,
    }
  }
  return base
}

export function serializeMarketAccess(value) {
  return normalizeMarketAccess(value)
}

export function emptyClinicalTrial() {
  return {
    id: null,
    code: '',
    start_plan: null,
    start_actual: null,
    end_plan: null,
    end_actual: null,
  }
}

export function normalizeClinicalTrials(list) {
  if (!Array.isArray(list) || !list.length) {
    return []
  }
  return list.map((item, index) => ({
    id: item?.id || null,
    _key: item?.id || item?._key || `ct-${index}`,
    code: String(item?.code || '').trim(),
    start_plan: item?.start_plan || item?.startPlan || null,
    start_actual: item?.start_actual || item?.startActual || null,
    end_plan: item?.end_plan || item?.endPlan || null,
    end_actual: item?.end_actual || item?.endActual || null,
  }))
}

/** Календарный день без сдвига UTC, как в поле даты. Неразобранное значение не затираем. */
function clinicalTrialDatePayload(value) {
  if (value == null || value === '') return null
  return expiryDatePayload(value) || value
}

export function serializeClinicalTrials(list) {
  return normalizeClinicalTrials(list)
    .filter(item =>
      item.code ||
      item.start_plan ||
      item.start_actual ||
      item.end_plan ||
      item.end_actual
    )
    .map(({ _key, ...item }) => ({
      ...item,
      start_plan: clinicalTrialDatePayload(item.start_plan),
      start_actual: clinicalTrialDatePayload(item.start_actual),
      end_plan: clinicalTrialDatePayload(item.end_plan),
      end_actual: clinicalTrialDatePayload(item.end_actual),
    }))
}

/** Статус подачи для прототипа: «Planned RU». */
export function smohStatusLabel(smoh) {
  if (!smoh) {
    return ''
  }
  if (smoh.status_label) {
    return String(smoh.status_label).trim()
  }
  const status = smoh.status
  const country = smoh.country
  const statusText = status?.value || status?.key || ''
  const countryText = country?.value || country?.key || ''
  return `${statusText} ${countryText}`.trim()
}

/** Пустая строка и прочерк не считаются заполненным прототипом. */
function meaningfulPrototypeText(value) {
  const text = relationText(value).trim()
  if (!text || text === '—' || text === '-') {
    return ''
  }
  return text
}

/**
 * Текст короткого прототипа из SoH (short → full → legacy).
 * Даты подачи сюда не входят: без текста прототипа блок на карточке не показывают.
 */
export function smohPrototypeShortText(smoh, preferEnglish) {
  if (!shouldShowSmohPrototype(smoh)) {
    return ''
  }
  const short = meaningfulPrototypeText(localizedPair(
    smoh.prototype_short_en,
    smoh.prototype_short_ru,
    preferEnglish
  ))
  if (short) {
    return short
  }
  return meaningfulPrototypeText(localizedPair(
    smoh.prototype_full_en || smoh.prototype_of_indications_en,
    smoh.prototype_full_ru || smoh.prototype_of_indications_ru,
    preferEnglish
  ))
}
